import React, { useCallback } from 'react';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import LogoutIcon from '@mui/icons-material/Logout';
import { authActions } from 'store/authSlice/authSlice';
import { useDispatch } from 'react-redux';
import styles from './userListDropDown.module.css';
import { GENERAL } from 'config';
import { openInNewTab } from 'utils/domHelper';
import { Tooltip } from '@mui/material';

export default function UserListDropDown({ dropDownItems, user }) {
    const dispatch = useDispatch();
    const userType = user?.roles?.[0] || '';

    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef(null);
    const logoutHandler = () => dispatch(authActions.logoutStart());

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    };

    function handleListKeyDown(event) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    }

    const prevOpen = React.useRef(open);
    React.useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current && anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    const changePwdHandler = useCallback(() => {
        openInNewTab(`${GENERAL.ADDRESSES.SSO}/password-change`);
    }, []);

    return (
        <Stack sx={{ padding: 0, display: 'flex' }} direction="row" spacing={2}>
            <Tooltip title="Always use your own account.">
                <Button
                    ref={anchorRef}
                    id="composition-button"
                    aria-controls={open ? 'composition-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    sx={{
                        '&.MuiButton-sizeMedium': {
                            borderRadius: '48px',
                            padding: '0.2rem 0.2rem 0.2rem 0.8rem',
                            backgroundColor: '#4685d32b',
                        },
                        '&:hover': {
                            outline: '1px solid #6a6a6aa9',
                        },
                    }}
                >
                    <div className={styles.userIdentity}>
                        <p className={styles.userName}>{user?.username || 'User'}</p>
                        <p className={styles.userRoles}>{userType}</p>
                    </div>
                    <div className={styles.styledAvatar}>{user?.username?.charAt(0).toUpperCase() || 'U'}</div>
                </Button>
            </Tooltip>
            <Popper
                open={open}
                style={{ zIndex: 100 }}
                className={styles.dropdownUserList}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom-start"
                transition
                disablePortal
            >
                {({ TransitionProps }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: 'right top',
                            marginRight: '24px',
                        }}
                    >
                        <Paper className={styles.dropdownPaper}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList
                                    className={styles.menuList}
                                    autoFocusItem={open}
                                    id="composition-menu"
                                    aria-labelledby="composition-button"
                                    onKeyDown={handleListKeyDown}
                                >
                                    {dropDownItems?.map((item) => (
                                        <MenuItem className={`${styles.menuItem} ${styles.profileItem}`} key={item.text} text={item.text}>
                                            <div className={styles.profileText}>
                                                <p className={styles.userName}>{item.text}</p>
                                                <p className={styles.userRoles}>{item.roles}</p>
                                            </div>
                                            <div className={styles.styledAvatarDropDown}>{user?.username?.charAt(0).toUpperCase() || 'U'}</div>
                                        </MenuItem>
                                    ))}

                                    <Divider className={styles.menuSectionDivider} />

                                    <MenuItem className={`${styles.menuItem} ${styles.changePasswordItem}`} onClick={changePwdHandler}>
                                        <div className={styles.menuItemAction}>
                                            <span className={styles.menuActionIcon}>
                                                <PermIdentityIcon fontSize="small" />
                                            </span>
                                            <span>Change password</span>
                                        </div>
                                    </MenuItem>

                                    <MenuItem className={`${styles.menuItem} ${styles.logoutItem}`} onClick={logoutHandler}>
                                        <div className={styles.menuItemAction}>
                                            <span className={styles.menuActionIcon}>
                                                <LogoutIcon fontSize="small" color="error" />
                                            </span>
                                            <p className={styles.logoutText}>Log out</p>
                                        </div>
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Stack>
    );
}
